import React, { useCallback, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import { format } from "date-fns";
import {
  ArrowRightIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

import "react-day-picker/dist/style.css";

const DISPLAY_FORMAT = "MM/dd/yyyy"; // MM/DD/YYYY per spec
const OUTPUT_FORMAT = "yyyy-MM-dd";

export interface DateRangeValue {
  startDate: string;
  endDate: string;
}

interface DateRangePickerProps {
  /** Called with a range when both dates are set and valid; null when incomplete or after deselection. */
  onRangeChange: (range: DateRangeValue | null) => void;
  tooltipId?: string;
  tooltipText?: string;
  /** Max date selectable for the start date (e.g. today). */
  maxStartDate?: Date;
  /** Max date selectable for the end date (e.g. today). */
  maxEndDate?: Date;
  /** When true, dates cannot be changed (e.g. locked until Clear). */
  readOnly?: boolean;
}

function toOutputFormat(date: Date): string {
  return format(date, OUTPUT_FORMAT);
}

function toDisplayString(date: Date): string {
  return format(date, DISPLAY_FORMAT);
}

export function DateRangePicker({
  onRangeChange,
  tooltipId,
  tooltipText,
  maxStartDate,
  maxEndDate,
  readOnly = false,
}: DateRangePickerProps) {
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const startContainerRef = useRef<HTMLDivElement>(null);
  const endContainerRef = useRef<HTMLDivElement>(null);

  /** Keeps parent in sync (react-day-picker single mode can deselect via second click → undefined). */
  const notifyParentRange = useCallback(
    (start: Date | undefined, end: Date | undefined) => {
      if (start && end && start <= end) {
        onRangeChange({
          startDate: toOutputFormat(start),
          endDate: toOutputFormat(end),
        });
      } else {
        onRangeChange(null);
      }
    },
    [onRangeChange]
  );

  const handleStartSelect = useCallback(
    (date: Date | undefined) => {
      setStartDate(date);
      setStartOpen(false);
      notifyParentRange(date, endDate);
    },
    [endDate, notifyParentRange]
  );

  const handleEndSelect = useCallback(
    (date: Date | undefined) => {
      setEndDate(date);
      setEndOpen(false);
      notifyParentRange(startDate, date);
    },
    [startDate, notifyParentRange]
  );

  const handleStartBlur = useCallback((e: React.FocusEvent) => {
    if (
      startContainerRef.current &&
      !startContainerRef.current.contains(e.relatedTarget as Node)
    ) {
      setStartOpen(false);
    }
  }, []);

  const handleEndBlur = useCallback((e: React.FocusEvent) => {
    if (
      endContainerRef.current &&
      !endContainerRef.current.contains(e.relatedTarget as Node)
    ) {
      setEndOpen(false);
    }
  }, []);

  const startDisabled = [
    ...(endDate ? [{ after: endDate }] : []),
    ...(maxStartDate ? [{ after: maxStartDate }] : []),
  ];

  const endDisabled = [
    ...(startDate ? [{ before: startDate }] : []),
    ...(maxEndDate ? [{ after: maxEndDate }] : []),
  ];

  return (
    <div
      className="flex flex-wrap items-center gap-2"
      role="group"
      aria-label="Date range"
    >
      <span className="flex items-center gap-1.5">
        <span className="text-sm font-semibold text-gray-700">Date range</span>
        {tooltipId && tooltipText && (
          <span data-tip data-for={tooltipId}>
            <InformationCircleIcon
              className="h-5 w-5 text-gray-500 cursor-help"
              aria-label="Date range filter information"
            />
          </span>
        )}
      </span>
      <div
        ref={startContainerRef}
        className="relative"
        onBlur={handleStartBlur}
      >
        <input
          type="text"
          readOnly
          value={startDate ? toDisplayString(startDate) : ""}
          placeholder="Start date"
          onClick={() => !readOnly && setStartOpen(true)}
          className={`text-sm font-medium text-gray-500 h-10 w-36 pr-8 pl-3 rounded z-0 border border-gray-300 focus:outline-none ${
            readOnly
              ? "cursor-default bg-gray-50"
              : "focus:ring-2 focus:ring-blue-500 cursor-pointer"
          }`}
          aria-label="Start date"
          aria-readonly={readOnly || undefined}
        />
        {startOpen && !readOnly && (
          <div className="absolute top-full left-0 mt-1 z-10 bg-white rounded-lg border border-gray-200 shadow-lg p-2">
            <DayPicker
              mode="single"
              selected={startDate}
              onSelect={handleStartSelect}
              disabled={startDisabled.length ? startDisabled : undefined}
              defaultMonth={startDate ?? endDate ?? new Date()}
            />
          </div>
        )}
      </div>
      <ArrowRightIcon
        className="h-5 w-5 text-gray-500 flex-shrink-0"
        aria-hidden
      />
      <div ref={endContainerRef} className="relative" onBlur={handleEndBlur}>
        <input
          type="text"
          readOnly
          value={endDate ? toDisplayString(endDate) : ""}
          placeholder="Finished date"
          onClick={() => !readOnly && setEndOpen(true)}
          className={`text-sm font-medium text-gray-500 h-10 w-36 pr-8 pl-3 rounded z-0 border border-gray-300 focus:outline-none ${
            readOnly
              ? "cursor-default bg-gray-50"
              : "focus:ring-2 focus:ring-blue-500 cursor-pointer"
          }`}
          aria-label="Finished date"
          aria-readonly={readOnly || undefined}
        />
        {endOpen && !readOnly && (
          <div className="absolute top-full left-0 mt-1 z-10 bg-white rounded-lg border border-gray-200 shadow-lg p-2">
            <DayPicker
              mode="single"
              selected={endDate}
              onSelect={handleEndSelect}
              disabled={endDisabled.length ? endDisabled : undefined}
              defaultMonth={endDate ?? startDate ?? new Date()}
            />
          </div>
        )}
      </div>
    </div>
  );
}
